import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";

/**
 * Toyota Customer Satisfaction Index (CSI) score logger.
 *
 * Only renders once a job is completed — no point asking for a score before
 * the customer has walked out. Advisor picks 1-10 (or clears). Persists via
 * `POST /api/jobs/:id/csi-score`. Feeds the "Last visit: 8/10 — beat it"
 * chip on the New Job modal's Customer Magic Points banner next time this
 * customer's reg is looked up.
 */
export default function CsiScorePanel({ job, onChanged }) {
  const [saving, setSaving] = useState(false);
  const [hoverScore, setHoverScore] = useState(null);
  const current = typeof job?.csi_score === "number" ? job.csi_score : null;

  if (job?.status !== "completed") return null;

  const save = async (score) => {
    setSaving(true);
    try {
      const r = await api.post(`/jobs/${job.id}/csi-score`, { score });
      toast.success(score === 0 ? "CSI score cleared" : `Toyota CSI logged: ${score}/10`);
      onChanged?.({ csi_score: r.data.csi_score, csi_score_at: r.data.csi_score_at });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save score");
    } finally {
      setSaving(false);
    }
  };

  const displayScore = hoverScore ?? current;
  const bandColour =
    displayScore === null || displayScore === undefined ? "text-muted-foreground" :
    displayScore >= 9 ? "text-emerald-600" :
    displayScore >= 7 ? "text-amber-600" :
    "text-red-600";

  return (
    <div className="card-default p-4" data-testid="csi-score-panel">
      <div className="flex items-center gap-2 mb-1">
        <Star className={`w-4 h-4 ${bandColour}`} />
        <div className="font-display font-bold text-base">Toyota CSI score</div>
        {current !== null && (
          <span className={`ml-auto text-xs font-mono-plex ${bandColour}`} data-testid="csi-current">
            Logged: {current}/10
          </span>
        )}
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Enter the Toyota CSI score once it arrives back from the survey.
        It&rsquo;ll pop up on the Customer Magic Points banner next time this reg is
        looked up so we know the score to beat.
      </p>
      <div className="flex flex-wrap gap-1.5" data-testid="csi-score-buttons">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
          const active = current === n;
          const band = n >= 9 ? "emerald" : n >= 7 ? "amber" : "red";
          const baseByBand = {
            emerald: active
              ? "bg-emerald-500 text-white border-emerald-600"
              : "hover:bg-emerald-500/10 hover:text-emerald-700 hover:border-emerald-500/40",
            amber: active
              ? "bg-amber-500 text-white border-amber-600"
              : "hover:bg-amber-500/10 hover:text-amber-700 hover:border-amber-500/40",
            red: active
              ? "bg-red-500 text-white border-red-600"
              : "hover:bg-red-500/10 hover:text-red-700 hover:border-red-500/40",
          }[band];
          return (
            <button
              key={n}
              onClick={() => save(n)}
              onMouseEnter={() => setHoverScore(n)}
              onMouseLeave={() => setHoverScore(null)}
              disabled={saving}
              className={`w-9 h-9 rounded-md border font-mono-plex text-sm font-semibold transition-colors ${baseByBand} ${active ? "" : "border-border text-foreground"}`}
              data-testid={`csi-score-btn-${n}`}
              title={`Log ${n}/10`}
            >
              {n}
            </button>
          );
        })}
        {current !== null && (
          <button
            onClick={() => save(0)}
            disabled={saving}
            className="px-2 h-9 rounded-md border border-border text-xs text-muted-foreground hover:bg-muted transition-colors"
            data-testid="csi-score-clear"
            title="Clear the logged score"
          >
            Clear
          </button>
        )}
        {saving && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground self-center ml-1" />}
      </div>
      {job.csi_score_at && (
        <div className="text-[10px] text-muted-foreground mt-2 font-mono">
          Recorded {new Date(job.csi_score_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
          {job.csi_score_by ? ` · by ${job.csi_score_by}` : ""}
        </div>
      )}
    </div>
  );
}
